/* Copyright (c) 2026. Amazon.com, Inc. or its affiliates. All rights reserved. */

import { createLogPrefix, globalRef, logError, type LogTarget } from '@amazon/vinyl-util'
import { selectedAgentProviderId } from '@/blackbolt/agent/agentProviderFlag'

const target: LogTarget = { logPrefix: createLogPrefix('agentWarmup') }

// ── Agent-connection warm-up seam (injection, not import) ─────────────────────
//
// Warming the selected chat provider (a lazy `@/bedrockAgent`
// registration) must be triggerable from the blackbolt tier — chat-input focus
// in `BottomToolbar` — WITHOUT a `blackbolt → <provider>` import. That back-edge
// would close the biref cycle the ratchet rejects (each provider already imports
// blackbolt). So `app.ts` (node `app`, no inbound edges)
// INJECTS the warmer via `registerAgentWarmer` and owns the dynamic import; the
// blackbolt tier only ever calls `ensureAgentConnected()`. Mirrors
// `agentProviderRegistry`'s one-way injection.

/**
 * The injected warm-up implementation. Receives the selected provider id so it
 * can dynamic-import the matching provider module. Returns void (not a promise):
 * it kicks off the async registration and routes its own rejection, so callers
 * on the focus hot path never float a promise.
 */
type AgentWarmer = (providerId: string) => void

let warmer: AgentWarmer | null = null
const warmLatchRef = globalRef<{ warmed: boolean }>(() => ({ warmed: false }))

/** Register the warm-up implementation. Called once at boot from `app.ts`. */
export function registerAgentWarmer(fn: AgentWarmer): void {
    warmer = fn
}

/**
 * Warm the selected chat-agent connection so the first turn skips its
 * connect cost. Called on every chat-input focus, so it must stay cheap on the
 * hot path and return synchronously (never blocks focus/typing).
 *
 * Two paths, by selected provider:
 *   - claw (id null): open the persistent claw session (init POST + WS) NOW so
 *     the first message rides a warm socket. `clawConnectionManager.prewarm()`
 *     is idempotent (no-op when already open/opening), so re-warming on later
 *     focuses — after the server closed the prior turn's socket — is cheap and
 *     wanted. The manager is dynamically imported so the claw wire stack stays
 *     off the boot chunk; the import kicks off async and this returns at once.
 *   - selected provider (bedrock): registration is warmed once through the
 *     injected `warmer` (no `blackbolt → @/bedrockAgent` import). Latched so
 *     repeated focus doesn't re-register; a later switch from claw still warms.
 */
export function ensureAgentConnected(): void {
    const id = selectedAgentProviderId()
    if (id === null) {
        import('@/blackbolt/agent/claw/clawConnectionManager')
            .then(({ clawConnectionManager }) => clawConnectionManager.prewarm())
            .catch((e) => logError(target, e))
        return
    }
    if (warmLatchRef.value.warmed || !warmer) return
    warmLatchRef.value.warmed = true
    warmer(id)
}
